import {
	SystemRole,
	type LoggedInUser,
	type ShipmentLog,
	type ShipmentLogDocumentKey,
	type TFSEntry,
	type TFSEntryMetadata,
} from "@/models"
import { isTFSActive, isTFSActiveOrUpcoming } from "../tfs-entries/permissions"

export function canShipmentsBeManaged(loggedInUser: LoggedInUser, tfs: TFSEntryMetadata | TFSEntry): boolean {
	return (isTFSActiveOrUpcoming(tfs) || loggedInUser.atLeastHasRole(SystemRole.SuperAdmin)) && !!tfs.consentIssued && !tfs.closed
}

export function allowedToManageShipments(loggedInUser: LoggedInUser, tfs: TFSEntryMetadata | TFSEntry): boolean {
	return loggedInUser.atLeastHasRole(SystemRole.User) && loggedInUser.canViewCompany(tfs.companyId)
}

export function canManageShipments(loggedInUser: LoggedInUser, tfs: TFSEntryMetadata | TFSEntry): boolean {
	return canShipmentsBeManaged(loggedInUser, tfs) && allowedToManageShipments(loggedInUser, tfs)
}

export function canCreateShipment(loggedInUser: LoggedInUser, tfs: TFSEntryMetadata): boolean {
	if (!canManageShipments(loggedInUser, tfs)) {
		return false
	}
	if (tfs.maximumShipments === null || tfs.maximumActiveShipments === null) {
		return false
	}
	if (tfs.totalShipments >= tfs.maximumShipments || tfs.activeShipments >= tfs.maximumActiveShipments) {
		return false
	}
	return true
}

export function canEditDocuments(options: {
	loggedInUser: LoggedInUser
	tfs: TFSEntryMetadata
	shipment: ShipmentLog
	type?: ShipmentLogDocumentKey
}): boolean {
	const { loggedInUser, tfs, shipment, type } = options

	if (type === "additionalDocuments") {
		if (tfs.closed) return loggedInUser.atLeastHasRole(SystemRole.Administrator)
		else return allowedToManageShipments(loggedInUser, tfs)
	}
	if (type === "recoveryAtFacility") {
		if (!isTFSActive(tfs)) return loggedInUser.atLeastHasRole(SystemRole.Administrator)
		else return isTFSActive(tfs) && allowedToManageShipments(loggedInUser, tfs)
	}

	if (!canManageShipments(loggedInUser, tfs)) {
		return false
	}

	if (shipment.cancelled || shipment.completed) {
		return false
	}

	return true
}

export function canEditShipmentDate(options: {
	loggedInUser: LoggedInUser
	tfs: TFSEntryMetadata
	shipment: ShipmentLog
}): boolean {
	const { loggedInUser, tfs, shipment } = options
	if (!canManageShipments(loggedInUser, tfs)) {
		return false
	}
	if (shipment.cancelled || shipment.completed) {
		return false
	}
	return true
}
